<template>
  <div class="xhs-comments">
    <div class="cmt-head">
      <span class="cmt-title">评论</span>
      <span class="cmt-count">{{ total }} 条</span>
      <el-button
        v-if="canLoad"
        text
        type="primary"
        size="small"
        class="cmt-reload"
        :loading="loading"
        @click="load"
      >刷新</el-button>
    </div>

    <div v-if="errorMsg" class="cmt-error">
      {{ errorMsg }}
      <el-button v-if="canLoad" link type="primary" size="small" @click="load">重试</el-button>
    </div>

    <div v-if="loading && !items.length" v-loading="loading" class="cmt-loading" />

    <template v-else-if="items.length">
      <div class="cmt-list">
        <XhsCommentRow
          v-for="c in items"
          :key="c.comment_id"
          :comment="c"
          @reply="openReply"
        />
      </div>
      <p v-if="!loading" class="cmt-foot">—— 已显示全部评论 ——</p>
    </template>

    <el-empty v-else-if="!loading && !errorMsg" description="还没有评论" :image-size="56" />

    <div v-if="replyOpen" class="cmt-replybox">
      <div class="rb-head">
        <span class="rb-tip" v-if="replyTarget">
          回复 {{ replyTarget.user_name }} 的评论
        </span>
        <span class="rb-tip" v-else>发表评论</span>
        <el-button text size="small" @click="closeReply">取消</el-button>
      </div>
      <el-input
        v-model="replyText"
        type="textarea"
        :rows="3"
        maxlength="1000"
        show-word-limit
        :placeholder="replyTarget ? '写下友善的回复…' : '说点什么…'"
      />
      <div class="rb-foot">
        <span class="rb-muted">将以 {{ meName || '当前账号' }} 身份发布</span>
        <el-button
          type="primary"
          size="small"
          :loading="posting"
          :disabled="!replyText.trim()"
          @click="submitReply"
        >发布</el-button>
      </div>
    </div>

    <div v-else-if="canReply" class="cmt-enter">
      <el-input
        v-model="replyText"
        placeholder="说点什么…"
        size="default"
        @keyup.enter.prevent="submitReply"
      >
        <template #append>
          <el-button :loading="posting" :disabled="!replyText.trim()" @click="submitReply">发布</el-button>
        </template>
      </el-input>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { xhsCommentApi } from '../../api'
import XhsCommentRow from './XhsCommentRow.vue'

const props = defineProps({
  /** 作品详情 DTO（normalizeContent 产物） */
  item: { type: Object, required: true },
})

const items = ref([])
const total = ref(0)
const loading = ref(false)
const errorMsg = ref('')
const replyOpen = ref(false)
const replyTarget = ref(null)
const replyText = ref('')
const posting = ref(false)

const accountId = computed(() => props.item?.accountId || props.item?.raw?.account_id || '')
const noteId = computed(() => props.item?.workId || props.item?.raw?.note_id || props.item?.raw?.work_id || '')
const noteUrl = computed(() => props.item?.noteUrl || props.item?.raw?.note_url || props.item?.raw?.link || '')
const meName = computed(() => props.item?.raw?.account_display_name || props.item?.accountDisplayName || '')
const canLoad = computed(() => Boolean(noteUrl.value || noteId.value))
const canReply = computed(() => Boolean(noteId.value))

async function load() {
  if (!canLoad.value) {
    errorMsg.value = '缺少账号或笔记信息，无法加载评论'
    return
  }
  loading.value = true
  errorMsg.value = ''
  try {
    const res = await xhsCommentApi.liveComments({
      account_id: accountId.value,
      note_url: noteUrl.value,
      note_id: noteId.value,
    })
    const data = res?.data || res
    items.value = data.items || []
    total.value = data.total ?? items.value.length
  } catch (e) {
    const detail = e?.response?.data?.detail || ''
    if (detail && /登录已过期|登录过期|会话.*过期|登录.*已失效/.test(detail)) {
      errorMsg.value = '小红书登录已过期，实时评论拉取失败。请到「账号作品」重新完成该账号的 PC 扫码登录后再试。'
    } else if (detail && /xsec_token|无法浏览/.test(detail)) {
      errorMsg.value = '该笔记来自创作后台，缺少访问凭证，无法实时拉取评论；可在「评论监控」中添加该笔记后查看。'
    } else {
      errorMsg.value = detail || '评论加载失败'
    }
  } finally {
    loading.value = false
  }
}

function openReply(c) {
  replyTarget.value = c || null
  replyText.value = ''
  replyOpen.value = true
}
function closeReply() {
  replyOpen.value = false
  replyTarget.value = null
  replyText.value = ''
}

async function submitReply() {
  const text = replyText.value.trim()
  if (!text) return
  if (!canReply.value) {
    ElMessage.warning('缺少笔记 ID，无法回复')
    return
  }
  posting.value = true
  try {
    await xhsCommentApi.liveReply({
      account_id: accountId.value,
      note_id: noteId.value,
      note_url: noteUrl.value,
      content: text,
      target_comment_id: replyTarget.value?.comment_id || '',
    })
    ElMessage.success('评论已发布')
    replyText.value = ''
    replyTarget.value = null
    replyOpen.value = false
    await load()
  } catch (e) {
    ElMessage.error(e?.response?.data?.detail || '发布失败')
  } finally {
    posting.value = false
  }
}

watch(
  () => [props.item?.workId, props.item?.noteUrl, props.item?.accountId],
  () => {
    if (props.item?.workId) load()
  },
  { immediate: true },
)

defineExpose({ load })
</script>

<style scoped>
.xhs-comments {
  padding: 14px 16px 18px;
  border-top: 8px solid #f5f5f5;
  background: #fff;
}
.cmt-head { display: flex; align-items: center; gap: 8px; margin-bottom: 6px; }
.cmt-title { font-size: 16px; font-weight: 700; color: #1a1a1a; }
.cmt-count { font-size: 13px; color: #999; }
.cmt-reload { margin-left: auto; }
.cmt-loading { min-height: 80px; }
.cmt-error {
  padding: 12px; background: #fdf6ec; border-radius: 8px;
  color: #e6a23c; font-size: 13px; display: flex; align-items: center; gap: 8px;
}
.cmt-list { display: flex; flex-direction: column; }
.cmt-foot { text-align: center; color: #c0c4cc; font-size: 12px; margin: 12px 0 0; }
.cmt-enter { margin-top: 14px; }
.cmt-replybox {
  margin-top: 14px; border: 1px solid #eee; border-radius: 10px;
  padding: 12px; background: #fafafa;
}
.rb-head { display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; }
.rb-tip { font-size: 13px; color: #666; font-weight: 600; }
.rb-foot { display: flex; justify-content: space-between; align-items: center; margin-top: 8px; }
.rb-muted { font-size: 12px; color: #a8abb2; }
</style>
